/*!

=========================================================
* Paper Dashboard PRO React - v1.3.2
=========================================================

* Product Page: https://www.creative-tim.com/product/paper-dashboard-pro-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import React, { Suspense } from "react";
import ReactDOM from "react-dom/client";
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  RouterProvider,
} from "react-router-dom";

// Import i18n configuration
import { i18nReady } from "./i18n";

import { initAnalytics } from "services/analytics.ts";

import AuthLayout from "layouts/Auth.js";
import { loadDashboard } from "layouts/loadDashboard";
import { LanguageProvider } from "contexts/LanguageContext";
import { ToastProvider } from "components/Manager/ToastContainer";
import ErrorBoundary from "components/ErrorBoundary";
import { lazyWithReload } from "utils/lazyWithReload";

import DevPanel from "components/Dev/DevPanel";

/**
 * The login page is the only layout in this bundle. The other three, and the
 * dashboard's global stylesheets (Bootstrap, Paper Dashboard, Tailwind), are
 * one chunk — see layouts/DashboardShell.js — so the most visited page does
 * not download the whole dashboard before it can paint. Keep it that way: a
 * static import of anything under layouts/ or views/ from here, or from the
 * login page, brings the dashboard back into the first download.
 */
const AdminLayout = lazyWithReload(() =>
  loadDashboard().then((m) => ({ default: m.AdminLayout }))
);
const ManagerLayout = lazyWithReload(() =>
  loadDashboard().then((m) => ({ default: m.ManagerLayout }))
);
const PublicLayout = lazyWithReload(() =>
  loadDashboard().then((m) => ({ default: m.PublicLayout }))
);

// Must run before render so the first pageview and any early event are captured.
initAnalytics();

/**
 * Root layout route. A data router renders only its route tree, so anything
 * that used to sit as a sibling of <Routes> (DevPanel) needs a real route to
 * live in. Each layout below still owns its own nested <Routes>; those become
 * descendant routes and keep working unchanged — while `useBlocker` now
 * resolves through the DataRouterContext this provider supplies.
 */
const RootLayout = () => (
  <>
    <Suspense fallback={null}>
      <Outlet />
    </Suspense>
    {process.env.NODE_ENV === "development" && <DevPanel />}
  </>
);

const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      // The live /landing never reaches React: it is a standalone page served
      // ahead of the app (src/landing/renderLanding.js). This is the previous
      // page, rendered only if that serving ever fails.
      { path: "/landing", element: <PublicLayout page="landing" /> },
      { path: "/app-link", element: <PublicLayout page="app-link" /> },
      // Legal pages. Public and unauthenticated on purpose: an app-store
      // reviewer, and anyone who has already uninstalled the app, has to be
      // able to reach these without a login. /delete-account in particular is
      // the URL Google Play's data-deletion requirement asks for.
      { path: "/terms", element: <PublicLayout page="terms" /> },
      { path: "/privacy", element: <PublicLayout page="privacy" /> },
      {
        path: "/delete-account",
        element: <PublicLayout page="delete-account" />,
      },
      // Welcome-credential claim link, reached from the WhatsApp template.
      // Short path on purpose: it has to fit a message button URL.
      { path: "/c/:token", element: <PublicLayout page="claim" /> },
      // Splat paths: each layout renders its own <Routes> for everything below.
      { path: "/auth/*", element: <AuthLayout /> },
      { path: "/manager/*", element: <ManagerLayout /> },
      { path: "/admin/*", element: <AdminLayout /> },
      { path: "*", element: <Navigate to="/auth/login" replace /> },
    ],
  },
]);

const root = ReactDOM.createRoot(document.getElementById("root"));
const render = () =>
  root.render(
    <LanguageProvider>
      <ToastProvider position="top-right">
        <ErrorBoundary>
          <RouterProvider router={router} />
        </ErrorBoundary>
      </ToastProvider>
    </LanguageProvider>
  );

// The translations are a chunk of their own (src/i18n/index.js): render once
// the active language is in, so no raw key ever flashes. A failed load still
// renders — keys beat a blank page.
i18nReady.then(render, render);
